import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  BellAlertIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  PlayIcon,
  StopIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { VpsPageHeader } from './vpsShared';
import AccountPicker from '../../../components/ScraperV2AccountPicker';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtMs(ms) {
  if (ms == null || ms < 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

function fmtTime(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function NotifBadge({ status }) {
  if (status === 'received') return <span className="text-green-600 dark:text-green-400">✅ Received</span>;
  if (status === 'silent') return <span className="text-red-600 dark:text-red-400">❌ Silent</span>;
  return <span className="text-gray-400">⏳ Pending</span>;
}

function DmBadge({ arrived }) {
  if (arrived === true) return <span className="text-green-600 dark:text-green-400">✅ Arrived</span>;
  if (arrived === false) return <span className="text-red-600 dark:text-red-400">❌ Missing</span>;
  return <span className="text-gray-400">⏳ Pending</span>;
}

function StatusBadge({ status }) {
  const map = {
    healthy: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    degraded: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    silent: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold capitalize ${map[status] || ''}`}>
      {status}
    </span>
  );
}

function ProgressBar({ title, segments, total, checkedLabel, loop, checker, onControl, controlBusy, extraStatus, hideStop, followbackDue }) {
  const safeTotal = Math.max(0, Number(total) || 0);
  const checked = segments
    .filter((s) => s.countsTowardChecked)
    .reduce((sum, s) => sum + (Number(s.value) || 0), 0);
  const pct = safeTotal > 0 ? Math.min(100, Math.round((checked / safeTotal) * 100)) : 0;

  const loopState = loop === 'notif' ? checker?.notif : loop === 'inbox' ? checker?.inbox : null;
  const enabledKey = loop === 'notif' ? 'notifEnabled' : loop === 'inbox' ? 'inboxEnabled' : null;
  const enabled = enabledKey ? checker?.[enabledKey] !== false && loopState?.phase !== 'stopped' : true;
  const followbackDueOnline = loop === 'followback'
    && Array.isArray(followbackDue)
    && followbackDue.some((d) => d.online && Number(d.due) > 0);
  const running = loop === 'followback'
    ? (
      !!segments.find((s) => s.key === 'dispatched' && Number(s.value) > 0)
      || followbackDueOnline
    )
    : (!!loopState?.running || loopState?.phase === 'checking');
  const inFlight = Array.isArray(loopState?.inFlight) ? loopState.inFlight : [];
  const workerAlive = checker?.workerAlive !== false;
  const statusLine = loop === 'followback'
    ? (extraStatus
      ? `${extraStatus} — Ryzen pulls jobs automatically; Recheck re-routes only`
      : 'GoLogin followbacks run on the VPS that owns each monitor')
    : !workerAlive && checker?.heartbeatAt == null
      ? 'Waiting for massdm worker heartbeat…'
      : !workerAlive
        ? 'Worker heartbeat stale — is massdm up?'
        : (loopState?.message || (enabled ? 'Idle' : 'Stopped'));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {checkedLabel || `${checked.toLocaleString()} / ${safeTotal.toLocaleString()} checked`} · {pct}%
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            disabled={controlBusy}
            onClick={() => onControl?.(loop, 'start')}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/40 dark:text-green-300 dark:hover:bg-green-900/60 disabled:opacity-50"
            title="Start / resume scanning"
          >
            <PlayIcon className="w-3.5 h-3.5" />
            Start
          </button>
          <button
            type="button"
            disabled={controlBusy}
            onClick={() => onControl?.(loop, 'recheck')}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/40 dark:text-indigo-300 dark:hover:bg-indigo-900/60 disabled:opacity-50"
            title={loop === 'followback' ? 'Requeue failed / make due now' : 'Requeue finished checks and run again'}
          >
            <ArrowPathIcon className={`w-3.5 h-3.5 ${running ? 'animate-spin' : ''}`} />
            Recheck
          </button>
          {!hideStop && (
            <button
              type="button"
              disabled={controlBusy}
              onClick={() => onControl?.(loop, 'stop')}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-900/60 disabled:opacity-50"
              title="Stop scanning"
            >
              <StopIcon className="w-3.5 h-3.5" />
              Stop
            </button>
          )}
        </div>
      </div>

      <div className="h-3 w-full rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 flex">
        {safeTotal === 0 ? null : segments.map((s) => {
          const w = Math.max(0, (Number(s.value) || 0) / safeTotal) * 100;
          if (w <= 0) return null;
          return (
            <div
              key={s.key}
              className={`${s.colorClass} h-full transition-[width] duration-300`}
              style={{ width: `${w}%` }}
              title={`${s.label}: ${(Number(s.value) || 0).toLocaleString()}`}
            />
          );
        })}
      </div>

      <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-300">
        {segments.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-2.5 h-2.5 rounded-sm ${s.colorClass}`} />
            {s.label}: {(Number(s.value) || 0).toLocaleString()}
          </span>
        ))}
      </div>

      <div className="mt-3 min-h-[4.5rem] rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2">
        <div className="flex items-center gap-2 text-xs">
          <span
            className={`inline-block w-2 h-2 rounded-full ${
              running ? 'bg-amber-400 animate-pulse' : enabled ? 'bg-green-500' : 'bg-gray-400'
            }`}
          />
          <span className="font-medium text-gray-700 dark:text-gray-200">
            {running ? 'Processing' : enabled ? 'Ready' : 'Stopped'}
          </span>
          <span className="text-gray-500 dark:text-gray-400 truncate">{statusLine}</span>
        </div>
        {running && loopState?.batchSize > 0 && (
          <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            Batch {loopState.completedInBatch || 0}/{loopState.batchSize}
            {loopState.dueCount != null ? ` · ${loopState.dueCount.toLocaleString()} due` : ''}
            {checker?.config?.inboxConcurrency && loop === 'inbox'
              ? ` · conc ${checker.config.inboxConcurrency} · pages ${checker.config.inboxMaxPages}`
              : null}
          </div>
        )}
        {inFlight.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {inFlight.map((item) => (
              <span
                key={item.id || `${item.from}-${item.to}`}
                className="inline-flex max-w-full truncate rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 px-1.5 py-0.5 text-[11px] font-mono text-gray-600 dark:text-gray-300"
              >
                @{item.from} → @{item.to}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Per-sender row in the timeline section ────────────────────────────────────

function SenderRow({ senderUsername, events }) {
  const [expanded, setExpanded] = useState(false);

  // Find first silent event
  const firstSilentIdx = events.findIndex((e) => e.notifStatus === 'silent');
  const received = events.filter((e) => e.notifStatus === 'received').length;
  const silent = events.filter((e) => e.notifStatus === 'silent').length;
  const total = events.length;

  // Build DM lookup by receiverUsername for this sender
  const hasDm = events.some((e) => e._dmEvent);

  const summary = `${total} injected · ✅ ${received} · ❌ ${silent}${hasDm ? ` · DMs tracked` : ''}`;

  return (
    <div className="border rounded-lg dark:border-gray-700 overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDownIcon className="w-4 h-4 text-gray-500" /> : <ChevronRightIcon className="w-4 h-4 text-gray-500" />}
          <span className="font-medium text-gray-800 dark:text-gray-200">@{senderUsername}</span>
          <span className="text-sm text-gray-500 dark:text-gray-400">{summary}</span>
        </div>
        {firstSilentIdx !== -1 && (
          <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 px-2 py-0.5 rounded">
            Silent from follow #{events[firstSilentIdx]?.senderCumulativeFollowCount ?? '?'}
          </span>
        )}
      </button>

      {expanded && (
        <div className="p-4">
          {firstSilentIdx !== -1 && (
            <div className="mb-3 text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded px-3 py-2 text-red-700 dark:text-red-300">
              Notifications stopped after follow #{events[firstSilentIdx].senderCumulativeFollowCount ?? '?'}{' '}
              (Round {events[firstSilentIdx].roundIdx}, inject #{events[firstSilentIdx].followInRoundIdx}).
              {events[firstSilentIdx].delayMsSincePrevCanary != null && (
                <> Delay at cutoff: {fmtMs(events[firstSilentIdx].delayMsSincePrevCanary)}.</>
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                  <th className="pb-2 pr-3">Follow #</th>
                  <th className="pb-2 pr-3">Round</th>
                  <th className="pb-2 pr-3">In-Round</th>
                  <th className="pb-2 pr-3">Sent At</th>
                  <th className="pb-2 pr-3">Delay Since Prev</th>
                  <th className="pb-2 pr-3">Notif</th>
                  <th className="pb-2 pr-3">Lag</th>
                  <th className="pb-2">DM</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev, idx) => {
                  const isSilence = ev.notifStatus === 'silent' && idx === firstSilentIdx;
                  return (
                    <tr
                      key={ev._id}
                      className={`border-b dark:border-gray-700 ${isSilence ? 'border-l-4 border-l-red-500 bg-red-50 dark:bg-red-900/10' : ''}`}
                    >
                      <td className="py-1.5 pr-3 font-mono">{ev.senderCumulativeFollowCount ?? idx + 1}</td>
                      <td className="py-1.5 pr-3">{ev.roundIdx}</td>
                      <td className="py-1.5 pr-3">{ev.followInRoundIdx}</td>
                      <td className="py-1.5 pr-3 font-mono">{fmtTime(ev.sentAt)}</td>
                      <td className="py-1.5 pr-3">{fmtMs(ev.delayMsSincePrevCanary)}</td>
                      <td className="py-1.5 pr-3"><NotifBadge status={ev.notifStatus} /></td>
                      <td className="py-1.5 pr-3">{ev.notifStatus === 'received' ? fmtMs(ev.notifLagMs) : '—'}</td>
                      <td className="py-1.5">
                        {ev._dmEvent ? <DmBadge arrived={ev._dmEvent.inboxArrived} /> : <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function VpsNotifHealth() {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();

  // Setup form state
  const [setupOpen, setSetupOpen] = useState(true);
  const [allAccounts, setAllAccounts] = useState([]);
  const [selectedMonitorIds, setSelectedMonitorIds] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  // Per-agent monitor-only toggle — Set of agent IDs that should ONLY follow monitors
  const [monitorOnlyAgentIds, setMonitorOnlyAgentIds] = useState(new Set());
  const [notifDelayMin, setNotifDelayMin] = useState(10);
  const [sessionLabel, setSessionLabel] = useState('');
  const [starting, setStarting] = useState(false);

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  // Timeline (drawer closed by default)
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [followEvents, setFollowEvents] = useState([]);
  const [dmEvents, setDmEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  // Panel C DM filter
  const [dmFilter, setDmFilter] = useState('all'); // 'all' | 'arrived' | 'missing'
  const [controlBusy, setControlBusy] = useState(false);

  // ── Stable loaders ────────────────────────────────────────────────────────────

  const loadSessions = useCallback((opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setSessionsLoading(true);
    api.get('/admin/canary/sessions')
      .then((r) => setSessions(r.data?.sessions || []))
      .catch(() => {})
      .finally(() => { if (!silent) setSessionsLoading(false); });
  }, []);

  const loadSessionData = useCallback((sessionId, { includeTimeline = false, silent = false } = {}) => {
    if (!sessionId) {
      setFollowEvents([]);
      setDmEvents([]);
      setStats(null);
      return Promise.resolve();
    }
    if (!silent) setStatsLoading(true);
    if (includeTimeline && !silent) setEventsLoading(true);

    const requests = [
      api.get('/admin/canary/stats', { params: { sessionId } }),
      api.get('/admin/canary/dm-events', { params: { sessionId, limit: 2000 } }),
    ];
    if (includeTimeline) {
      requests.push(api.get('/admin/canary/events', { params: { sessionId, limit: 5000 } }));
    }

    return Promise.all(requests)
      .then((results) => {
        const [statRes, dmRes, evRes] = results;
        setStats(statRes.data || null);
        setDmEvents(dmRes.data?.events || []);
        if (evRes) setFollowEvents(evRes.data?.events || []);
        setLastRefreshedAt(new Date());
      })
      .catch(() => {})
      .finally(() => {
        if (!silent) setStatsLoading(false);
        if (includeTimeline && !silent) setEventsLoading(false);
      });
  }, []);

  // ── Load accounts + agents on mount ──────────────────────────────────────────

  useEffect(() => {
    if (user?.role !== 'admin') return;
    api.get('/accounts', { params: { limit: 500, filterStatus: 'active' } })
      .then((r) => setAllAccounts(r.data?.accounts || r.data || []))
      .catch(() => {});
    api.get('/admin/va/ai-agents', { params: { compact: 1 } })
      .then((r) => setAgents(r.data?.agents || []))
      .catch(() => {});
    loadSessions();
  }, [user?.role, loadSessions]);

  // ── Load / poll session stats when selected ───────────────────────────────────

  const timelineOpenRef = React.useRef(timelineOpen);
  timelineOpenRef.current = timelineOpen;

  useEffect(() => {
    if (!selectedSessionId) {
      setFollowEvents([]);
      setDmEvents([]);
      setStats(null);
      setTimelineOpen(false);
      return undefined;
    }
    setFollowEvents([]);
    loadSessionData(selectedSessionId, { includeTimeline: false });
    const id = setInterval(() => {
      loadSessions({ silent: true });
      loadSessionData(selectedSessionId, { includeTimeline: timelineOpenRef.current, silent: true });
    }, 15_000);
    return () => clearInterval(id);
  }, [selectedSessionId, loadSessionData, loadSessions]);

  // Load heavy timeline only when drawer opens
  useEffect(() => {
    if (!selectedSessionId || !timelineOpen) return;
    loadSessionData(selectedSessionId, { includeTimeline: true });
  }, [timelineOpen, selectedSessionId, loadSessionData]);

  // ── Start session ─────────────────────────────────────────────────────────────

  const handleStartSession = async () => {
    if (!selectedMonitorIds.length) { alert('Pick at least one monitor account.'); return; }
    if (!selectedAgentIds.length) { alert('Pick at least one VA agent.'); return; }
    setStarting(true);
    try {
      await api.post('/admin/canary/sessions', {
        label: sessionLabel || `Canary ${new Date().toLocaleDateString()}`,
        agentIds: selectedAgentIds,
        monitorOnlyAgentIds: [...monitorOnlyAgentIds],
        monitorAccountIds: selectedMonitorIds,
        notifCheckDelayMs: (notifDelayMin || 10) * 60_000,
      });
      loadSessions();
      setSetupOpen(false);
    } catch (err) {
      alert(err?.response?.data?.error || err.message);
    } finally {
      setStarting(false);
    }
  };

  const handleStopSession = async (id) => {
    if (!window.confirm('Stop this canary session? The agent injection will be disabled.')) return;
    await api.post(`/admin/canary/sessions/${id}/stop`).catch(() => {});
    loadSessions();
  };

  const handleResumeSession = async (id) => {
    if (!window.confirm('Resume this canary session? Injection will be re-enabled on its agents.')) return;
    try {
      await api.post(`/admin/canary/sessions/${id}/resume`);
      loadSessions();
    } catch (err) {
      alert(err?.response?.data?.error || err.message);
    }
  };

  const handleCheckerControl = async (loop, action) => {
    setControlBusy(true);
    try {
      if (loop === 'followback') {
        const res = await api.post('/admin/canary/followbacks/kick', {
          sessionId: selectedSessionId || undefined,
        });
        if (selectedSessionId) {
          await loadSessionData(selectedSessionId, { includeTimeline: timelineOpenRef.current });
        }
        return;
      }
      const res = await api.post(`/admin/canary/checker/${loop}/${action}`);
      if (res.data?.status) {
        setStats((prev) => (prev ? { ...prev, checker: res.data.status } : { checker: res.data.status }));
      }
      if (selectedSessionId) {
        await loadSessionData(selectedSessionId, { includeTimeline: timelineOpenRef.current });
      }
    } catch (err) {
      alert(err?.response?.data?.error || err.message);
    } finally {
      setControlBusy(false);
    }
  };

  // ── Group follow events by sender for timeline view ───────────────────────────

  const dmByFollowEvent = useMemo(() => {
    const map = {};
    for (const dm of dmEvents) {
      if (dm.canaryFollowEventId) map[dm.canaryFollowEventId] = dm;
    }
    return map;
  }, [dmEvents]);

  const eventsBySender = useMemo(() => {
    const map = {};
    for (const ev of followEvents) {
      const key = ev.senderUsername;
      if (!map[key]) map[key] = [];
      map[key].push({ ...ev, _dmEvent: dmByFollowEvent[ev._id] || null });
    }
    return map;
  }, [followEvents, dmByFollowEvent]);

  // ── Chart data ────────────────────────────────────────────────────────────────

  const chartColors = isDarkMode
    ? { received: 'rgba(74, 222, 128, 0.7)', silent: 'rgba(248, 113, 113, 0.7)', bg: '#1f2937' }
    : { received: 'rgba(34, 197, 94, 0.7)', silent: 'rgba(239, 68, 68, 0.7)', bg: '#ffffff' };

  const barChartData = useMemo(() => {
    if (!stats?.bucketData?.length) return null;
    return {
      labels: stats.bucketData.map((b) => b.label),
      datasets: [
        {
          label: 'Notif received',
          data: stats.bucketData.map((b) => b.received),
          backgroundColor: chartColors.received,
        },
        {
          label: 'Silent',
          data: stats.bucketData.map((b) => b.total - b.received),
          backgroundColor: chartColors.silent,
        },
      ],
    };
  }, [stats, chartColors.received, chartColors.silent]);

  const chartOptions = {
    responsive: true,
    plugins: { legend: { position: 'top' }, title: { display: false } },
    scales: {
      x: { stacked: true, ticks: { color: isDarkMode ? '#9ca3af' : '#6b7280' } },
      y: { stacked: true, beginAtZero: true, ticks: { color: isDarkMode ? '#9ca3af' : '#6b7280' } },
    },
  };

  const filteredDmEvents = useMemo(() => {
    if (dmFilter === 'arrived') return dmEvents.filter((d) => d.inboxArrived === true);
    if (dmFilter === 'missing') return dmEvents.filter((d) => d.inboxArrived === false);
    return dmEvents;
  }, [dmEvents, dmFilter]);

  const notifProgress = stats?.progress?.notif || { total: 0, pending: 0, received: 0, silent: 0, checked: 0 };
  const dmProgress = stats?.progress?.dm || { total: 0, pending: 0, arrived: 0, missing: 0, checked: 0 };
  const fbProgress = stats?.progress?.followback || {
    total: 0, pending: 0, dispatched: 0, done: 0, failed: 0, checked: 0, dueByAgent: [],
  };
  const checker = stats?.checker || null;
  const followbackStatusLine = (fbProgress.dueByAgent || [])
    .slice(0, 3)
    .map((d) => `${d.agentName} ${d.due} due${d.online ? '' : ' ⚠ offline'}`)
    .join(' · ') || null;

  const activeSessions = sessions.filter((s) => s.status === 'active');
  const selectedSession = sessions.find((s) => s._id === selectedSessionId) || null;

  if (user?.role !== 'admin') {
    return <div className="p-8 text-center text-gray-500">Admin access required.</div>;
  }

  return (
    <div className="space-y-6 pb-12">
      <VpsPageHeader
        title="Notif Health Monitor"
        subtitle="Inject monitor accounts into live follow rounds to measure when X stops delivering follow notifications."
        actions={
          <div className="flex items-center gap-2">
            {lastRefreshedAt && (
              <span className="text-xs text-gray-400">Updated {fmtTime(lastRefreshedAt)}</span>
            )}
            <button
              onClick={() => {
                loadSessions();
                if (selectedSessionId) {
                  loadSessionData(selectedSessionId, { includeTimeline: timelineOpen });
                }
              }}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Refresh"
            >
              <ArrowPathIcon className={`w-4 h-4 text-gray-500 ${statsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        }
      />

      {/* ── Session Setup Panel ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
        <button
          className="w-full flex items-center justify-between px-5 py-4 text-left"
          onClick={() => setSetupOpen((v) => !v)}
        >
          <div className="flex items-center gap-2">
            <PlayIcon className="w-5 h-5 text-indigo-500" />
            <span className="font-semibold text-gray-800 dark:text-gray-100">Setup New Session</span>
            {activeSessions.length > 0 && (
              <span className="ml-2 text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded-full px-2 py-0.5">
                {activeSessions.length} active
              </span>
            )}
          </div>
          {setupOpen ? <ChevronDownIcon className="w-4 h-4 text-gray-400" /> : <ChevronRightIcon className="w-4 h-4 text-gray-400" />}
        </button>

        {setupOpen && (
          <div className="px-5 pb-5 border-t border-gray-100 dark:border-gray-700 pt-4 space-y-5">
            {/* Monitor Account Picker */}
            <AccountPicker
              title="Monitor Accounts"
              accounts={allAccounts}
              selectedIds={selectedMonitorIds}
              onChange={setSelectedMonitorIds}
              required
            />

            {/* Agent Picker */}
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                VA Agents <span className="text-red-500">*</span>
                <span className="font-normal text-gray-500 ml-1">({selectedAgentIds.length} selected)</span>
                <span className="font-normal text-gray-400 ml-2 text-xs">— tick "Only" to make an agent follow monitors exclusively</span>
              </div>
              <div className="max-h-52 overflow-auto border rounded-lg p-2 dark:border-gray-700 space-y-1">
                {agents.length === 0 ? (
                  <p className="text-sm text-gray-500 p-2">No agents found.</p>
                ) : (
                  <>
                    {/* Header row */}
                    <div className="flex items-center gap-2 px-2 pb-1 border-b dark:border-gray-700 text-xs text-gray-400 select-none">
                      <span className="w-4" />
                      <span className="flex-1">Agent</span>
                      <span className="w-14 text-center">Status</span>
                      <span className="w-16 text-center" title="Monitor-only: this agent follows ONLY your monitor accounts">Monitor only</span>
                    </div>
                    {agents.map((ag) => {
                      const selected = selectedAgentIds.includes(ag._id);
                      const monitorOnly = monitorOnlyAgentIds.has(ag._id);
                      return (
                        <div key={ag._id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                          {/* Select checkbox */}
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => {
                              setSelectedAgentIds((prev) =>
                                e.target.checked ? [...prev, ag._id] : prev.filter((id) => id !== ag._id)
                              );
                              if (!e.target.checked) {
                                setMonitorOnlyAgentIds((prev) => {
                                  const next = new Set(prev);
                                  next.delete(ag._id);
                                  return next;
                                });
                              }
                            }}
                            className="rounded border-gray-300 flex-shrink-0"
                          />
                          <span className="text-sm text-gray-800 dark:text-gray-200 flex-1 truncate">{ag.name}</span>
                          <span className={`w-14 text-center text-xs px-1.5 py-0.5 rounded ${
                            ag.status === 'running'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                              : 'bg-gray-100 text-gray-500 dark:bg-gray-700'
                          }`}>{ag.status}</span>
                          {/* Monitor-only toggle — only enabled when agent is selected */}
                          <div className="w-16 flex justify-center">
                            <button
                              type="button"
                              disabled={!selected}
                              onClick={() => {
                                setMonitorOnlyAgentIds((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(ag._id)) next.delete(ag._id);
                                  else next.add(ag._id);
                                  return next;
                                });
                              }}
                              className={`text-xs px-2 py-0.5 rounded font-medium transition-colors ${
                                !selected
                                  ? 'opacity-30 cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-700'
                                  : monitorOnly
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40'
                              }`}
                              title={selected ? (monitorOnly ? 'Click to switch back to injection mode' : 'Click to enable monitor-only mode for this agent') : 'Select this agent first'}
                            >
                              {monitorOnly ? 'Only ✓' : 'Only'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
              {/* Mode summary */}
              {selectedAgentIds.length > 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  {[...monitorOnlyAgentIds].filter((id) => selectedAgentIds.includes(id)).length > 0
                    ? `${[...monitorOnlyAgentIds].filter((id) => selectedAgentIds.includes(id)).length} monitor-only · ${selectedAgentIds.length - [...monitorOnlyAgentIds].filter((id) => selectedAgentIds.includes(id)).length} injection`
                    : `${selectedAgentIds.length} injection (random positions per round)`
                  }
                </p>
              )}
            </div>

            {/* Config Inputs */}
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Notif check delay (min)</span>
                <span className="text-xs text-gray-400 ml-1">after each injection</span>
                <input
                  type="number"
                  min={1}
                  max={120}
                  value={notifDelayMin}
                  onChange={(e) => setNotifDelayMin(Number(e.target.value))}
                  className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Session label</span>
                <input
                  type="text"
                  placeholder="Optional"
                  value={sessionLabel}
                  onChange={(e) => setSessionLabel(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </label>
            </div>

            <button
              onClick={handleStartSession}
              disabled={starting}
              className="px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {starting ? 'Starting…' : 'Start Session'}
            </button>
          </div>
        )}
      </div>

      {/* ── Active Sessions List ── */}
      {sessions.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Sessions</div>
          <div className="space-y-2">
            {sessionsLoading && <p className="text-sm text-gray-400">Loading…</p>}
            {sessions.map((s) => (
              <div
                key={s._id}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                  selectedSessionId === s._id
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750'
                }`}
                onClick={() => setSelectedSessionId(s._id === selectedSessionId ? '' : s._id)}
              >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  s.status === 'active' ? 'bg-green-500' : s.status === 'paused' ? 'bg-yellow-500' : 'bg-gray-400'
                }`} />
                <span className="text-sm font-medium text-gray-800 dark:text-gray-100 flex-1">
                  {s.label || `Session ${fmtDate(s.createdAt)}`}
                </span>
                <span className="text-xs text-gray-500">
                  {s.followEvents ?? 0} follows · {s.notifReceived ?? 0} notifs · {s.dmEvents ?? 0} DMs
                  {s.dmArrived != null ? ` · ${s.dmArrived} arrived` : ''}
                </span>
                <span className="text-xs text-gray-400">{fmtDate(s.createdAt)}</span>
                {s.status === 'active' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleStopSession(s._id); }}
                    className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-colors"
                    title="Stop session"
                  >
                    <StopIcon className="w-4 h-4" />
                  </button>
                )}
                {(s.status === 'stopped' || s.status === 'paused') && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleResumeSession(s._id); }}
                    className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 transition-colors"
                    title="Resume session"
                  >
                    <PlayIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedSessionId && (
        <>
          {selectedSession?.status === 'active' && (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
              <div className="text-sm text-amber-900 dark:text-amber-100">
                <span className="font-medium">Follow injection is running</span>
                <span className="text-amber-700 dark:text-amber-200 ml-2">
                  — monitor accounts are being injected into live follow rounds on this session&apos;s agents.
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleStopSession(selectedSessionId)}
                className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
              >
                Stop injection
              </button>
            </div>
          )}
          {selectedSession?.status === 'stopped' && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
              Follow injection is <span className="font-medium text-gray-800 dark:text-gray-100">stopped</span>
              {' '}for this session. GoLogin followbacks and inbox checks on queued events continue.
            </div>
          )}

          {/* ── Check progress bars ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ProgressBar
              title="Follow notification checks"
              loop="notif"
              checker={checker}
              onControl={handleCheckerControl}
              controlBusy={controlBusy}
              total={notifProgress.total}
              segments={[
                { key: 'received', label: 'Received', value: notifProgress.received, colorClass: 'bg-green-500', countsTowardChecked: true },
                { key: 'silent', label: 'Silent', value: notifProgress.silent, colorClass: 'bg-red-500', countsTowardChecked: true },
                { key: 'pending', label: 'Pending', value: notifProgress.pending, colorClass: 'bg-gray-300 dark:bg-gray-600', countsTowardChecked: false },
              ]}
            />
            <ProgressBar
              title="GoLogin followbacks"
              loop="followback"
              checker={checker}
              onControl={handleCheckerControl}
              controlBusy={controlBusy}
              hideStop
              extraStatus={followbackStatusLine}
              followbackDue={fbProgress.dueByAgent}
              total={fbProgress.total}
              checkedLabel={`${(fbProgress.checked || 0).toLocaleString()} / ${(fbProgress.total || 0).toLocaleString()} finished`}
              segments={[
                { key: 'done', label: 'Done', value: fbProgress.done, colorClass: 'bg-green-500', countsTowardChecked: true },
                { key: 'failed', label: 'Failed', value: fbProgress.failed, colorClass: 'bg-red-500', countsTowardChecked: true },
                { key: 'dispatched', label: 'In flight', value: fbProgress.dispatched, colorClass: 'bg-amber-400', countsTowardChecked: false },
                { key: 'pending', label: 'Pending', value: fbProgress.pending, colorClass: 'bg-gray-300 dark:bg-gray-600', countsTowardChecked: false },
              ]}
            />
            <ProgressBar
              title="DM inbox checks"
              loop="inbox"
              checker={checker}
              onControl={handleCheckerControl}
              controlBusy={controlBusy}
              total={dmProgress.total}
              segments={[
                { key: 'arrived', label: 'Arrived', value: dmProgress.arrived, colorClass: 'bg-green-500', countsTowardChecked: true },
                { key: 'missing', label: 'Missing', value: dmProgress.missing, colorClass: 'bg-red-500', countsTowardChecked: true },
                { key: 'pending', label: 'Pending', value: dmProgress.pending, colorClass: 'bg-gray-300 dark:bg-gray-600', countsTowardChecked: false },
              ]}
            />
          </div>

          {/* ── Stat Panels A / B / C (above timeline) ── */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            {/* Panel A: Notification rate by cumulative follow count */}
            <div className="xl:col-span-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
              <div className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-3">
                A — Notif Rate by Follow Count
              </div>
              {statsLoading && !barChartData ? (
                <div className="h-40 flex items-center justify-center">
                  <ArrowPathIcon className="w-6 h-6 text-gray-300 animate-spin" />
                </div>
              ) : barChartData ? (
                <Bar data={barChartData} options={chartOptions} />
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">No data yet</p>
              )}
              <p className="text-xs text-gray-400 mt-2">
                X-axis: cumulative follows at time of injection · Bars show received vs silent
              </p>
            </div>

            {/* Panel B: Account health table */}
            <div className="xl:col-span-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
              <div className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-3">
                B — Sender Account Health
              </div>
              {statsLoading && !stats?.accounts?.length ? (
                <div className="h-40 flex items-center justify-center">
                  <ArrowPathIcon className="w-6 h-6 text-gray-300 animate-spin" />
                </div>
              ) : stats?.accounts?.length ? (
                <div className="overflow-auto max-h-72">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                        <th className="pb-1.5 text-left pr-3">Account</th>
                        <th className="pb-1.5 text-right pr-3">Inj.</th>
                        <th className="pb-1.5 text-right pr-3">Rate</th>
                        <th className="pb-1.5 text-right pr-3">First silent</th>
                        <th className="pb-1.5 text-right">Health</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.accounts.map((acc) => (
                        <tr key={acc.senderUsername} className="border-b dark:border-gray-700">
                          <td className="py-1 pr-3 font-medium">@{acc.senderUsername}</td>
                          <td className="py-1 pr-3 text-right">{acc.totalInjected}</td>
                          <td className="py-1 pr-3 text-right">{acc.notifRate}%</td>
                          <td className="py-1 pr-3 text-right">{acc.firstSilentAt ?? '—'}</td>
                          <td className="py-1 text-right"><StatusBadge status={acc.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">No data yet</p>
              )}
            </div>

            {/* Panel C: DM inbox delivery log */}
            <div className="xl:col-span-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold text-sm text-gray-700 dark:text-gray-300">
                  C — DM Inbox Log
                </div>
                <div className="flex gap-1">
                  {['all', 'arrived', 'missing'].map((f) => (
                    <button
                      key={f}
                      onClick={() => setDmFilter(f)}
                      className={`text-xs px-2 py-0.5 rounded ${
                        dmFilter === f
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>
              {statsLoading && !dmEvents.length ? (
                <div className="h-40 flex items-center justify-center">
                  <ArrowPathIcon className="w-6 h-6 text-gray-300 animate-spin" />
                </div>
              ) : filteredDmEvents.length ? (
                <div className="overflow-auto max-h-72 space-y-1">
                  {filteredDmEvents.map((dm) => (
                    <div
                      key={dm._id}
                      className="flex items-center gap-2 text-xs px-2 py-1.5 rounded border dark:border-gray-700"
                    >
                      <span className="font-medium text-gray-700 dark:text-gray-300 truncate flex-1">
                        @{dm.senderUsername} → @{dm.receiverUsername}
                      </span>
                      <span className="text-gray-400 whitespace-nowrap">{fmtTime(dm.dmSentAt)}</span>
                      <DmBadge arrived={dm.inboxArrived} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">
                  {dmFilter !== 'all' ? `No "${dmFilter}" DM events` : 'No DM events yet'}
                </p>
              )}
            </div>
          </div>

          {/* ── Per-Sender Timeline (drawer, closed by default) ── */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
            <button
              type="button"
              className="w-full flex items-center justify-between px-5 py-4 text-left"
              onClick={() => setTimelineOpen((v) => !v)}
            >
              <div className="flex items-center gap-2">
                {timelineOpen
                  ? <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                  : <ChevronRightIcon className="w-4 h-4 text-gray-500" />}
                <BellAlertIcon className="w-5 h-5 text-indigo-500" />
                <span className="font-semibold text-gray-800 dark:text-gray-100">
                  Per-Sender Timeline
                </span>
                <span className="text-xs text-gray-400">
                  {Object.keys(eventsBySender).length
                    ? `${Object.keys(eventsBySender).length} senders`
                    : 'open to load'}
                </span>
                {eventsLoading && <ArrowPathIcon className="w-4 h-4 text-gray-400 animate-spin" />}
              </div>
              <span className="text-xs text-indigo-600 dark:text-indigo-400">
                {timelineOpen ? 'Close drawer' : 'Open drawer'}
              </span>
            </button>

            {timelineOpen && (
              <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4 space-y-3 max-h-[70vh] overflow-y-auto">
                <div className="flex justify-end">
                  <button
                    onClick={() => loadSessionData(selectedSessionId, { includeTimeline: true })}
                    className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Refresh timeline"
                  >
                    <ArrowPathIcon className={`w-4 h-4 text-gray-500 ${eventsLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                {Object.keys(eventsBySender).length === 0 && !eventsLoading && (
                  <p className="text-sm text-gray-500 text-center py-6">
                    No follow events yet for this session. Events appear once the injection hook fires inside a live follow job.
                  </p>
                )}
                {Object.entries(eventsBySender).map(([sender, events]) => (
                  <SenderRow key={sender} senderUsername={sender} events={events} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
